/**
 * QA Panel — PrizePop controller (Campaigns / PrizePop sub-tab).
 *
 * Mirrors `campaignsController.ts` (which owns the Tournaments sub-tab), but
 * targets the campaigns feature's `campaignsStore` rather than the tournaments
 * store. Lets QA take over the prize_pop slice for an editing session — fetch
 * a real list with pasted creds, edit entries in place, and inject synthetic
 * mocks alongside them.
 *
 * Flow:
 *   - `enableMocking()` sets `campaignsStore.mockMode = true`. The companion
 *     guard in `setCampaignsFromResponse` and the gates in `useCampaignsQuery`
 *     / sub-fetch hooks then drop real refetches.
 *   - `fetchExternal(sessionKey, playerId)` runs a one-off `fetch()` against
 *     the public PrizePop endpoint with QA-pasted creds and seeds the store
 *     via `seedExternalCampaigns` (which bypasses the mockMode early-return
 *     that blocks `setCampaignsFromResponse`).
 *   - `createMockCampaign(form)` appends a synthetic campaign with id
 *     `qa-mock-<timestamp>`. mockMode is NOT auto-flipped — the UI gates
 *     this behind enableMocking, so the flag is already on by the time we
 *     get here.
 *   - `updateCampaignField(id, patch)` patches a single campaign in place
 *     (no resort).
 *   - `disableMocking()` clears every mock and disables `mockMode` so real
 *     polling resumes; in-place edits to real entries are silently lost on
 *     the next poll, which is expected.
 *
 * Boundary note: imports from `features/campaigns` strictly via the public
 * `index.ts`. The qaPanel build flag is dead-stripped in prod; we therefore
 * must NOT add any imports the other way (campaigns → qaPanel).
 */

import {
    useCampaignsStore,
    PRIZE_POP_CAMPAIGN_TYPE,
    isPrizePopSource,
    PrizePopStatuses,
} from '../../campaigns';
import type {
    CampaignSource,
    IPrizePopCampaign,
    IPrizePopResources,
    CampaignLevel,
    PrizePopFormula,
    PrizePopInterval,
} from '../../campaigns';
import { useAppStore } from 'store/app/appStore';

/* ── Public types surfaced to the popup ────────────────────────────── */

export interface CreateMockPrizePopForm {
    name: string;
    terms: string;
    status: PrizePopStatuses;
    level: CampaignLevel;
    formula: PrizePopFormula;
    minSpinNumber: number;
    minStake: number;
    baseCurrency: string;
    /** All offsets are in MINUTES relative to "now" at submit time. */
    startOffsetMin: number;
    endOffsetMin: number;
    prizeFund: number;
    totalPrizesCount: number;
    /** Game keys to include. Defaults seeded from MOCK_PRIZEPOP_GAMES below. */
    games: string[];
}

export interface MockedPrizePopRef {
    id: string;
    name: string;
}

/**
 * Compact, transferable view of a PrizePop campaign for the popup. Mirrors
 * `TournamentSummary`'s flattening pattern — keep the popup-facing shape
 * lean and free of `IFormulaParams` / Map fields.
 */
export interface PrizePopCampaignSummary {
    id: string;
    name: string;
    terms: string;
    status: PrizePopStatuses;
    level: CampaignLevel;
    formula: PrizePopFormula;
    minSpinNumber: number;
    minStake: number;
    baseCurrency: string;
    startDate: string;
    endDate: string;
    visibleEndDate: string;
    prizeFund: number;
    totalPrizesCount: number;
    prizesWonCount: number;
    joined: boolean;
    joinedPlayersCount: number;
    games: string[];
    /** True when the id starts with `'qa-mock-'`. */
    isMock: boolean;
}

export interface PrizePopState {
    /** Whether the campaigns store is currently in mock mode. */
    mockMode: boolean;
    /** Light list of campaigns injected via this panel. */
    mocks: MockedPrizePopRef[];
    /** Editable summaries of every PrizePop campaign currently in the store. */
    campaigns: PrizePopCampaignSummary[];
    /** Best guess at the player's currency at the time of read. */
    defaultBaseCurrency: string;
}

export interface FetchExternalResult {
    ok: boolean;
    /** HTTP status; 0 means network/CORS error, no response received. */
    status?: number;
    message?: string;
    /** Number of campaigns seeded on success. */
    count?: number;
}

/* ── Hard-coded reference data ─────────────────────────────────────── */

/**
 * Hardcoded resource bundle for synthetic PrizePop campaigns. The QA URLs
 * are reused from the Tournaments controller — the rendering components
 * are robust to "wrong-shaped" image URLs and using these everywhere lets
 * QA visually confirm a mock is active.
 */
const FILL_COLOR = '#111113';
const BORDER_COLOR = '#B16C04';

const CAMPAIGNS_PLATE =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlate1775220633493.webp?v=1775220633493';
const CAMPAIGNS_PLATE_CLOSE =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlateClose1775220637944.webp?v=1775220637944';
const CAMPAIGNS_PLATE_CLOSE_LOGO =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsPlateCloseLogo1775220646332.webp?v=1775220646332';
const CAMPAIGNS_MULTIVIEW_TRANSPARENT =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloTransparentImage1775220624945.webp?v=1775220624944';
const CAMPAIGNS_MULTIVIEW_TRANSPARENT_DESKTOP =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloTransparentImageDesktop1775220629233.webp?v=1775220629233';
const CAMPAIGNS_MULTIVIEW_PORTRAIT =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloSeparatedBackgroundPortrait1775220621095.webp?v=1775220621095';
const CAMPAIGNS_MULTIVIEW_LANDSCAPE =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsMultiViewSoloSeparatedBackgroundLandscape1775220617304.webp?v=1775220617304';
const CAMPAIGNS_SINGLEVIEW_CLOSE =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundClose1775220661731.webp?v=1775220661730';
const CAMPAIGNS_SINGLEVIEW_PORTRAIT =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundPortrait1775220668438.webp?v=1775220668438';
const CAMPAIGNS_SINGLEVIEW_LANDSCAPE =
    'https://resourceservicecdn-dev.egt-ong.com/qa/templates/CampaignsSingleViewSoloBackgroundLandscape1775220665222.webp?v=1775220665222';

function buildMockResources(): IPrizePopResources {
    return {
        CampaignsPlate: CAMPAIGNS_PLATE,
        CampaignsPlateClose: CAMPAIGNS_PLATE_CLOSE,
        CampaignsPlateCloseLogo: CAMPAIGNS_PLATE_CLOSE_LOGO,
        CampaignsMultiViewSoloTransparentImage: CAMPAIGNS_MULTIVIEW_TRANSPARENT,
        CampaignsMultiViewSoloTransparentImageDesktop: CAMPAIGNS_MULTIVIEW_TRANSPARENT_DESKTOP,
        CampaignsMultiViewSoloSeparatedBackgroundPortrait: CAMPAIGNS_MULTIVIEW_PORTRAIT,
        CampaignsMultiViewSoloSeparatedBackgroundLandscape: CAMPAIGNS_MULTIVIEW_LANDSCAPE,
        CampaignsSingleViewSoloBackgroundClose: CAMPAIGNS_SINGLEVIEW_CLOSE,
        CampaignsSingleViewSoloBackgroundPortrait: CAMPAIGNS_SINGLEVIEW_PORTRAIT,
        CampaignsSingleViewSoloBackgroundLandscape: CAMPAIGNS_SINGLEVIEW_LANDSCAPE,
        FillColor: FILL_COLOR,
        BorderColor: BORDER_COLOR,
    };
}

/**
 * 50 EGT game keys used as the default participating-games set for any mock
 * PrizePop campaign. Mirrors the Tournaments controller's MOCK_TOURNAMENT_GAMES
 * — duplicated rather than imported to keep the two QA controllers isolated.
 * Game keys not present in `appStore.supportedGames` for a given operator are
 * silently dropped by the rendering layer.
 */
const MOCK_PRIZEPOP_GAMES: string[] = [
    'ARSlot', 'BCSlot', 'BCBSlot', 'FBHRSlot', 'FVBPSlot',
    'FBRSlot', 'FVCLHSlot', 'FVCHTSlot', 'FVCHSlot', 'FDCSlot',
    'FDHSlot', 'FVDRSlot', 'FVJHSlot', 'FVLRSlot', 'FVSHRSlot',
    'FVSHSlot', 'FVSFSlot', 'FVSPSlot', 'FVSRSlot', 'TADSlot',
    'TBHRSlot', 'TNBPSlot', 'TNBRSlot', 'TNCLHSlot', 'TCHSlot',
    'TCHBSlot', 'TCWSlot', 'TNDCSlot', 'TNDHSlot', 'TNDRSlot',
    'TNJHSlot', 'TNJRSlot', 'TNLHSlot', 'TNLRSlot', 'TSDSlot',
    'TNSHRSlot', 'TNSHSlot', 'TNSFSlot', 'TNSPSlot', 'TNSRSlot',
    'OBCSlot', 'OBHSlot', 'OHBPSlot', 'OHBRSlot', 'OHCLHSlot',
    'OHCHTSlot', 'OHCHSlot', 'OSFSlot', 'OSHSlot', 'OSHSSlot',
];

/* ── Form-default helpers ──────────────────────────────────────────── */

function getDefaultBaseCurrency(): string {
    return useAppStore.getState().balance?.currency ?? 'EUR';
}

// fallow-ignore-next-line unused-export
export function buildDefaultPrizePopForm(): CreateMockPrizePopForm {
    const baseCurrency = getDefaultBaseCurrency();
    return {
        name: 'QA Test PrizePop',
        terms: '',
        status: PrizePopStatuses.ACTIVE,
        level: 'PURPLE',
        formula: 'RANDOM',
        minSpinNumber: 25,
        minStake: 25,
        baseCurrency,
        startOffsetMin: 0,
        endOffsetMin: 240,
        prizeFund: 500,
        totalPrizesCount: 100,
        games: MOCK_PRIZEPOP_GAMES.slice(),
    };
}

/* ── Campaign builder ──────────────────────────────────────────────── */

function offsetIso(now: number, offsetMin: number): string {
    return new Date(now + offsetMin * 60 * 1000).toISOString();
}

/** How long (minutes) a COMPLETED_VISIBLE mock stays listed after its interval ended. */
const COMPLETED_VISIBLE_TAIL_MIN = 240;

/**
 * Align the start/end offsets with the chosen status. The lobby derives a
 * PrizePop's rendered state purely from `intervals` vs. now (`status` is
 * informational / a sort tiebreaker), so a COMPLETED_VISIBLE mock whose
 * interval still overlaps "now" renders as ACTIVE. Preserves the entered
 * duration and only shifts when the offsets contradict the status.
 */
function deriveOffsetsForStatus(
    status: PrizePopStatuses,
    startOffsetMin: number,
    endOffsetMin: number,
): { startOffsetMin: number; endOffsetMin: number } {
    const duration = Math.max(endOffsetMin - startOffsetMin, 1);
    if (status === PrizePopStatuses.COMPLETED_VISIBLE && endOffsetMin >= 0) {
        // Interval must be fully in the past so `allIntervalsEnded` is true.
        return { startOffsetMin: -5 - duration, endOffsetMin: -5 };
    }
    if (status === PrizePopStatuses.PROMO && startOffsetMin <= 0) {
        // Interval must be fully in the future so `isPromo` is true.
        return { startOffsetMin: 60, endOffsetMin: 60 + duration };
    }
    return { startOffsetMin, endOffsetMin };
}

/**
 * Single mock interval covering startDate→endDate. The lobby's intervals
 * consumer reads `range.startRangeDate` / `range.endRangeDate` and the
 * `hoursRange` array — an empty `hoursRange` is treated as "all hours".
 */
function buildMockIntervals(startIso: string, endIso: string): PrizePopInterval[] {
    return [
        {
            range: {
                startRangeDate: startIso,
                endRangeDate: endIso,
            },
            hoursRange: [
                {
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                },
            ],
        },
    ];
}

/**
 * Build a fully-formed `IPrizePopCampaign` ready to be pushed through
 * `addMockCampaign`. Note: PrizePop's `games` field is the canonical
 * `string[]` shape — no parsing step like tournaments needs.
 */
// fallow-ignore-next-line unused-export
export function buildMockPrizePopCampaign(form: CreateMockPrizePopForm): IPrizePopCampaign {
    const id = 'qa-mock-' + Date.now().toString();
    const now = Date.now();
    const { startOffsetMin, endOffsetMin } = deriveOffsetsForStatus(
        form.status,
        form.startOffsetMin,
        form.endOffsetMin,
    );
    const startDate = offsetIso(now, startOffsetMin);
    const endDate = offsetIso(now, endOffsetMin);
    // A finished mock keeps a future visibleEndDate so it mirrors a real COMPLETED_VISIBLE payload.
    const visibleEndDate = endOffsetMin < 0 ? offsetIso(now, COMPLETED_VISIBLE_TAIL_MIN) : endDate;

    return {
        id,
        name: form.name,
        terms: form.terms,
        status: form.status,
        startDate,
        endDate,
        visibleEndDate,
        prizeFund: form.prizeFund,
        level: form.level,
        games: form.games.slice(),
        formula: form.formula,
        formulaParams: null,
        minSpinNumber: form.minSpinNumber,
        minStake: form.minStake,
        baseCurrency: form.baseCurrency,
        joined: false,
        resources: buildMockResources(),
        intervals: buildMockIntervals(startDate, endDate),
        joinedPlayersCount: 0,
        totalPrizesCount: form.totalPrizesCount,
        prizesWonCount: 0,
        type: PRIZE_POP_CAMPAIGN_TYPE,
    };
}

/* ── Controller ───────────────────────────────────────────────────── */

export class PrizePopController {
    private onChange: (() => void) | null = null;
    private storeUnsubscribe: (() => void) | null = null;

    init(onChange: () => void): void {
        this.onChange = onChange;
        // Re-broadcast whenever the campaigns list or mockMode flag changes
        // so the popup's list stays live (e.g. if QA seeds via paste-creds,
        // the popup updates).
        this.storeUnsubscribe = useCampaignsStore.subscribe((state, prev) => {
            if (state.mockMode !== prev.mockMode || state.campaigns !== prev.campaigns) {
                this.onChange?.();
            }
        });
    }

    destroy(): void {
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;
        this.onChange = null;
    }

    getState(): PrizePopState {
        const store = useCampaignsStore.getState();
        const popList = store.campaigns.filter(isPrizePopSource) as IPrizePopCampaign[];
        const mocks: MockedPrizePopRef[] = popList
            .filter((c) => c.id.startsWith('qa-mock-'))
            .map((c) => ({ id: c.id, name: c.name }));
        const campaigns: PrizePopCampaignSummary[] = popList.map(toSummary);
        return {
            mockMode: store.mockMode,
            mocks,
            campaigns,
            defaultBaseCurrency: getDefaultBaseCurrency(),
        };
    }

    /**
     * Enable mockMode without touching the campaign list. The companion
     * guards in `setCampaignsFromResponse` and `useCampaignsQuery` then
     * drop real refetches; the popup is free to seed/edit/add at will.
     */
    enableMocking(): void {
        useCampaignsStore.getState().setMockMode(true);
    }

    /**
     * Disable mockMode + clear every mock entry. Real polling resumes; the
     * next refetch silently overwrites any in-place edits to real entries —
     * that's expected (the editing session is over).
     */
    disableMocking(): void {
        useCampaignsStore.getState().clearMockCampaigns();
    }

    /**
     * One-shot, QA-driven external fetch using the user-pasted creds. The
     * URL is hard-coded to the public `prize-pop-service-qa-ext.egt-ong.com`
     * host — this does NOT share auth with the lobby's normal campaigns
     * fetch path. On success the store is seeded via `seedExternalCampaigns`,
     * which is the only door not gated by mockMode.
     *
     * Endpoint analog: `tournaments-ong-qa-ext.egt-ong.com` for tournaments.
     * Host and path derived from `prizePopConfig` + the default `prizePopHost`
     * baked into `appStore` / `iframe-loader`.
     */
    async fetchExternal(sessionKey: string, playerId: string): Promise<FetchExternalResult> {
        const url =
            'https://prize-pop-service-qa-ext.egt-ong.com/prizepopservice/api/game-client/prize-pops/get-all' +
            `?sessionKey=${encodeURIComponent(sessionKey)}&playerId=${encodeURIComponent(playerId)}` +
            '&stripResourcesHost=true';

        let response: Response;
        try {
            response = await fetch(url, { mode: 'cors' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Network error';
            return { ok: false, status: 0, message };
        }

        if (!response.ok) {
            let message = response.statusText || 'Request failed';
            try {
                const body = (await response.json()) as { code?: string; message?: string };
                if (body && typeof body.message === 'string' && body.message.length > 0) {
                    message = body.message;
                }
            } catch {
                /* ignore — non-JSON body */
            }
            return { ok: false, status: response.status, message };
        }

        let body: { campaigns?: CampaignSource[] };
        try {
            body = (await response.json()) as { campaigns?: CampaignSource[] };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Invalid JSON';
            return { ok: false, status: response.status, message };
        }

        const list = Array.isArray(body.campaigns) ? body.campaigns : [];
        // Note: real responses are normally run through `buildResourceUrls` to
        // expand relative `resources.*` paths against `assetsHost`. We pass
        // `stripResourcesHost=true` on the request, so we receive expanded
        // URLs only when the backend was started without that capability —
        // either shape works here because the QA panel doesn't re-render any
        // resources that depend on assetsHost prefixing. If a future panel
        // tab needs that prefixing, plumb `assetsHost` through and call
        // `buildResourceUrls` per entry before seeding.
        useCampaignsStore.getState().seedExternalCampaigns(list);
        return { ok: true, status: response.status, count: list.length };
    }

    /**
     * Build a campaign from the form payload and inject it. Returns the
     * generated id so the popup can reflect "just created" feedback.
     */
    createMockCampaign(form: CreateMockPrizePopForm): string {
        const campaign = buildMockPrizePopCampaign(form);
        useCampaignsStore.getState().addMockCampaign(campaign);
        return campaign.id;
    }

    /** Remove a single mocked campaign by id. */
    removeMockCampaign(id: string): void {
        if (!id.startsWith('qa-mock-')) return;
        useCampaignsStore.getState().removeCampaign(id);
    }

    /**
     * In-place patch of a single campaign (real or mocked). Thin wrapper
     * around the store's `updateCampaignById` action — does NOT resort.
     */
    updateCampaignField(id: string, patch: Partial<IPrizePopCampaign>): void {
        useCampaignsStore.getState().updateCampaignById(id, patch);
    }

    /** Clear every mock and resume real polling. */
    clearAll(): void {
        useCampaignsStore.getState().clearMockCampaigns();
    }
}

/* ── Summary helpers ──────────────────────────────────────────────── */

function toSummary(c: IPrizePopCampaign): PrizePopCampaignSummary {
    return {
        id: c.id,
        name: c.name,
        terms: c.terms,
        status: c.status,
        level: c.level,
        formula: c.formula,
        minSpinNumber: c.minSpinNumber,
        minStake: c.minStake,
        baseCurrency: c.baseCurrency,
        startDate: c.startDate,
        endDate: c.endDate,
        visibleEndDate: c.visibleEndDate,
        prizeFund: c.prizeFund,
        totalPrizesCount: c.totalPrizesCount,
        prizesWonCount: c.prizesWonCount,
        joined: c.joined,
        joinedPlayersCount: c.joinedPlayersCount,
        games: Array.isArray(c.games) ? c.games.slice() : [],
        isMock: c.id.startsWith('qa-mock-'),
    };
}
